import { Box, CircularProgress, Grid, MenuItem, TextField } from '@material-ui/core'
import { ExpansionAutocomplete } from 'components/controls/misc/ExpansionAutocomplete'
import { Button as InventoryButton } from 'components/facets/Button'
import { TablePaginationControls } from 'components/facets/TablePaginationControls'
import { useUser } from 'components/providers/UserProvider'
import { loadInventoryTransactions } from 'lib/api'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnyYesNo,
  CardLanguage,
  CARD_LANGUAGES,
  ChannelFilterOptions,
  ConditionEnum,
  CONDITIONS,
  COUNTRY_EDITION,
  Expansion,
  FINISH_TYPES_CONVERSIONS,
  formatDateToYYYYMMDD,
  getNDaysAgo,
  INVENTORY_TRANSACTION_TYPE,
} from 'shared'
import { Filter } from '../stockPricing/FilterDock/Filter'
import './inventory-transactions.css'
import { PriceTransactionsTable } from './PriceTransactionsTable'
import { QuantityTransactionsTable } from './QuantityTransactionsTable'
import { ReservationTransactionsTable } from './ReservationTransactionsTable'
import { TaxTransactionsTable } from './TaxTransactionsTable'

export interface TransactionLogInterface {
  createdAt: Date
  processedAt: Date | null
  lastFailedAt: Date | null
  lastError: string | null
  name: string
  expansionName: string
  expansionCode: string
  mkmProductId: number
  cn: number
  idGame: number
  categoryName: string
  idLanguage: number
  condition: string
  finishType: string
  countryEdition: COUNTRY_EDITION
  isFirstEd: false
  isSigned: false
  channel: string
  cause: string | null
  priceFrom: number
  priceTo: number
  currency: string
}

// userId: the backoffice reads that user's transactions instead of the logged one's
export const InventoryTransactionsPresenter = ({ userId }: { userId?: string }): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { enqueueSnackbar } = useSnackbar()

  const [quantityTransactions, setQuantityTransactions] = useState<TransactionLogInterface[]>([])
  const [priceTransactions, setPriceTransactions] = useState<TransactionLogInterface[]>([])
  const [reservationTransactions, setReservationTransactions] = useState<TransactionLogInterface[]>(
    []
  )
  const [taxTransactions, setTaxTransactions] = useState<TransactionLogInterface[]>([])
  const [showLoading, setShowLoading] = useState(false)

  // quantity transactions pagination
  const [currentPageQuantity, setCurrentPageQuantity] = useState<number>(1)
  const [isLastPageQuantity, setIsLastPageQuantity] = useState<boolean>(false)

  // price transactions pagination
  const [currentPagePrice, setCurrentPagePrice] = useState<number>(1)
  const [isLastPagePrice, setIsLastPagePrice] = useState<boolean>(false)

  // reservation transactions pagination
  const [currentPageReservation, setCurrentPageReservation] = useState<number>(1)
  const [isLastPageReservation, setIsLastPageReservation] = useState<boolean>(false)

  // tax transactions pagination
  const [currentPageTax, setCurrentPageTax] = useState<number>(1)
  const [isLastPageTax, setIsLastPageTax] = useState<boolean>(false)

  // filters
  const defaultFilterValues = {
    itemName: '',
    expansionCode: '',
    processedAtFrom: getNDaysAgo(30),
    processedAtTo: new Date(),
    condition: [0, 6],
    idLanguage: 'any',
    finishType: AnyYesNo.Any,
    reverseHolo: AnyYesNo.Any,
    firstEd: AnyYesNo.Any,
    signed: AnyYesNo.Any,
    channel: ChannelFilterOptions.powertools,
    failed: AnyYesNo.Any,
  }
  const [itemName, setItemName] = useState<string>(defaultFilterValues.itemName)
  const [expansionCode, setExpansionCode] = useState<string>(defaultFilterValues.expansionCode)
  const [processedAtFrom, setProcessedAtFrom] = useState<Date>(defaultFilterValues.processedAtFrom)
  const [processedAtTo, setProcessedAtTo] = useState<Date>(defaultFilterValues.processedAtTo)

  const [condition, setCondition] = useState<number[]>(defaultFilterValues.condition)
  const [validConditions, setValidConditions] = useState<string[]>([])
  const [idLanguage, setIdLanguage] = useState<CardLanguage | 'any'>(
    defaultFilterValues.idLanguage as CardLanguage | 'any'
  )
  const [finishType, setFinishType] = useState<string>(AnyYesNo.Any)
  const [firstEd, setFirstEd] = useState<AnyYesNo>(defaultFilterValues.firstEd)
  const [signed, setSigned] = useState<AnyYesNo>(defaultFilterValues.signed)
  const [failed, setFailed] = useState<AnyYesNo>(defaultFilterValues.failed)
  const [channel, setChannel] = useState<ChannelFilterOptions>(defaultFilterValues.channel)

  const pageSize = 25

  const getYesNoAnyValue = (field: AnyYesNo) => {
    if (field === AnyYesNo.Any) {
      return undefined
    } else if (field === AnyYesNo.Yes) {
      return true
    } else {
      return false
    }
  }

  const fetchInventoryTransactions = async (type: string, page: number) => {
    const msPerDay = 1000 * 60 * 60 * 24

    const startUTC = Date.UTC(
      processedAtFrom.getFullYear(),
      processedAtFrom.getMonth(),
      processedAtFrom.getDate()
    )

    const endUTC = Date.UTC(
      processedAtTo.getFullYear(),
      processedAtTo.getMonth(),
      processedAtTo.getDate()
    )

    const days = (endUTC - startUTC) / msPerDay // avoid problems due to the change of daylight saving time

    if (days > 30) {
      if (type === INVENTORY_TRANSACTION_TYPE.QUANTITY) {
        enqueueSnackbar(t('transaction.invalidTimeRangeGreaterThan30'), { variant: 'error' })
      }
      return
    }

    if (days < 0) {
      if (type === INVENTORY_TRANSACTION_TYPE.QUANTITY) {
        enqueueSnackbar(t('transaction.invalidNegativeTimeRange'), { variant: 'error' })
      }
      return
    }

    setShowLoading(true)
    const params = {
      idLanguage: idLanguage === 'any' ? undefined : idLanguage,
      expansionCode: expansionCode === '' ? undefined : expansionCode,
      itemName: itemName === '' ? undefined : itemName,
      conditions: validConditions,
      finishType:
        finishType === 'any'
          ? undefined
          : FINISH_TYPES_CONVERSIONS.find((entry) => entry.label === finishType)!.code,
      isFirstEd: getYesNoAnyValue(firstEd),
      isSigned: getYesNoAnyValue(signed),
      minProcessedAt: processedAtFrom,
      maxProcessedAt: processedAtTo,
      // Any: no filter at all. "Only processed" also drops the failed ones, so it is sent only for No
      onlyProcessed: failed === AnyYesNo.No ? true : undefined,
      onlyFailed: failed === AnyYesNo.Yes ? true : undefined,
      size: pageSize,
      channel: channel,
      userId,
    }
    const paramsQuantity = {
      ...params,
      type: INVENTORY_TRANSACTION_TYPE.QUANTITY,
      page,
    }
    const paramsPrice = {
      ...params,
      type: INVENTORY_TRANSACTION_TYPE.PRICE,
      page,
    }
    const paramsReservation = {
      ...params,
      type: INVENTORY_TRANSACTION_TYPE.RESERVATION,
      page,
    }
    const paramsTax = {
      ...params,
      type: INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION,
      page,
    }

    if (type === INVENTORY_TRANSACTION_TYPE.QUANTITY) {
      const newQuantityTransactions = await loadInventoryTransactions(paramsQuantity)
      const totalCountQuantity = newQuantityTransactions.length
      setQuantityTransactions(newQuantityTransactions)
      setIsLastPageQuantity(totalCountQuantity < pageSize)
    } else if (type === INVENTORY_TRANSACTION_TYPE.PRICE) {
      const newPriceTransactions = await loadInventoryTransactions(paramsPrice)
      const totalCountPrice = newPriceTransactions.length
      setPriceTransactions(newPriceTransactions)
      setIsLastPagePrice(totalCountPrice < pageSize)
    } else if (type === INVENTORY_TRANSACTION_TYPE.RESERVATION) {
      const newReservationTransactions = await loadInventoryTransactions(paramsReservation)
      const totalCountReservation = newReservationTransactions.length
      setReservationTransactions(newReservationTransactions)
      setIsLastPageReservation(totalCountReservation < pageSize)
    } else if (type === INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION) {
      const newTaxTransactions = await loadInventoryTransactions(paramsTax)
      const totalCountTax = newTaxTransactions.length
      setTaxTransactions(newTaxTransactions)
      setIsLastPageTax(totalCountTax < pageSize)
    }

    setShowLoading(false)
  }

  useEffect(() => {
    const minCondition = condition[0]
    const maxCondition = condition[1]

    const newValidConditions = Object.keys(ConditionEnum).filter((key) => {
      const conditionValue = ConditionEnum[key as keyof typeof ConditionEnum]
      return conditionValue >= minCondition && conditionValue <= maxCondition
    })
    setValidConditions(newValidConditions)
  }, [condition])

  // New filters change how many pages there are, so every table starts again from the first one
  const handleFilterTransactions = async () => {
    setCurrentPageQuantity(1)
    setCurrentPagePrice(1)
    setCurrentPageReservation(1)
    setCurrentPageTax(1)
    await Promise.all([
      fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.QUANTITY, 1),
      fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.PRICE, 1),
      fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.RESERVATION, 1),
      fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION, 1),
    ])
  }

  useEffect(() => {
    handleFilterTransactions()
  }, [])

  const allExpansionsOption = {
    name: t('addArticles.allExpansions'),
    code: '',
    _id: 0,
  } as Expansion

  const anyYesNoOptions = [
    <MenuItem key={'any'} value={AnyYesNo.Any}>
      {t('filters.any')}
    </MenuItem>,
    <MenuItem key={'false'} value={AnyYesNo.No}>
      {t('filters.no')}
    </MenuItem>,
    <MenuItem key={'true'} value={AnyYesNo.Yes}>
      {t('filters.yes')}
    </MenuItem>,
  ]

  const activeChannelsForUser = Object.values(ChannelFilterOptions).filter((channel) => {
    if (
      ['powertools', 'cardmarket', 'cardtrader', 'shopify'].includes(channel) ||
      user.featureFlags?.[channel] === true
    ) {
      return true
    }
  })

  const channelOptions = activeChannelsForUser.map((channel) => (
    <MenuItem key={channel} value={channel}>
      {t(`common.channels.${channel}`)}
    </MenuItem>
  ))

  return (
    <div className="inventory-transactions-root p-4">
      <Box padding={2}>
        <h4 className="m-0 p-0">{t('filters.title')}</h4>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={4} lg={3} xl={3}>
            <TextField
              data-testid="item-name-filter"
              type="text"
              label="Search"
              fullWidth
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <Box marginTop="11px">
              <ExpansionAutocomplete
                data-testid="expansion-filter"
                value={expansionCode}
                options={[allExpansionsOption]}
                onChange={(e) => setExpansionCode(e.code)}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <Box>
              <TextField
                fullWidth
                label="Processed at - from"
                type="date"
                defaultValue={formatDateToYYYYMMDD(processedAtFrom)}
                onChange={(e) => setProcessedAtFrom(new Date(e.target.value))}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <Box>
              <TextField
                fullWidth
                label="Processed at - to"
                type="date"
                defaultValue={formatDateToYYYYMMDD(processedAtTo)}
                onChange={(e) => setProcessedAtTo(new Date(e.target.value))}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <Box>
              <Filter
                type="select"
                menuitems={anyYesNoOptions}
                setFilter={(_name, newValue) => setFailed(newValue)}
                defaultValue={failed}
                label="Failed"
                name={'failed'}
                activeFilter={false}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
            <Filter
              data-testid="condition-slider"
              type="slider"
              sliderProps={{
                valueLabelDisplay: 'auto',
                valueLabelFormat: (key: number) => CONDITIONS[key],
                step: 1,
                marks: true,
                min: 0,
                max: 6,
              }}
              defaultValue={condition}
              name="condition"
              setFilter={(_name, newValue) => setCondition(newValue)}
              label={t('card.condition')}
              activeFilter={false}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
            <Filter
              data-testid="language-filter"
              type="select"
              menuitems={[
                <MenuItem key={'any'} value={'any'}>
                  {t('filters.any')}
                </MenuItem>,

                ...CARD_LANGUAGES.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                )),
              ]}
              defaultValue={idLanguage}
              setFilter={(_name, newValue) => setIdLanguage(newValue)}
              label={t('card.language')}
              name="language"
              activeFilter={false}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
            <Filter
              type="select"
              menuitems={channelOptions}
              setFilter={(_name, newValue) => setChannel(newValue)}
              defaultValue={channel}
              label={t(`common.channel`)}
              name={'channel'}
              activeFilter={false}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
            <Filter
              data-testid="finishType-filter"
              type="select"
              menuitems={[
                <MenuItem key={'any'} value={'any'}>
                  {t('filters.any')}
                </MenuItem>,
                ...FINISH_TYPES_CONVERSIONS.map((finishType) => (
                  <MenuItem key={finishType.label} value={finishType.label}>
                    {finishType.label}
                  </MenuItem>
                )),
              ]}
              setFilter={(_name, newValue) => setFinishType(newValue)}
              defaultValue={finishType}
              label={t(`filters.finishType`)}
              name={'finishType'}
              activeFilter={false}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
            <Filter
              type="select"
              menuitems={anyYesNoOptions}
              setFilter={(_name, newValue) => setFirstEd(newValue)}
              defaultValue={finishType}
              label={t(`filters.firstEd`)}
              name={'firstEd'}
              activeFilter={false}
            />
          </Grid>

          <Grid item xs={6} sm={2} md={2} lg={1} xl={1}>
            <Filter
              type="select"
              menuitems={anyYesNoOptions}
              setFilter={(_name, newValue) => setSigned(newValue)}
              defaultValue={signed}
              label={t(`filters.signed`)}
              name={'signed'}
              activeFilter={false}
            />
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <InventoryButton
              data-testid="search-btn"
              fullWidth
              variant="contained"
              onClick={() => handleFilterTransactions()}
            >
              {t('table.search')}
            </InventoryButton>
          </Grid>
          {/*
            <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
              <CSVLink
                data={quantityTransactionsCsvData}
                asyncOnClick={true}
                onClick={(_event, done) => {
                  handleTransactionsDownload(done)
                }}
                enclosingCharacter={`"`}
                filename={`quantity-transactions-${formatDateToYYYYMMDD(new Date())}`}
                style={{ textDecoration: 'none' }}
              >
                <InventoryButton data-testid="export-csv-btn" fullWidth variant="contained">
                  Download CSV
                </InventoryButton>
              </CSVLink>
            </Grid>
          */}

          {/* <Grid item xs={12} sm={6} md={4} lg={2} xl={2}>
            <InventoryButton fullWidth variant="contained" onClick={() => handleResetFilters()}>
              {t('filters.resetFilters')}
            </InventoryButton>
          </Grid> */}
        </Grid>
      </Box>

      {showLoading ? (
        <div className="w-100 d-flex flex-column align-items-center ">
          <h6 className="pb-5">{t('common.loading')}</h6>
          <CircularProgress size={100} />
        </div>
      ) : (
        <>
          <Box padding={2}>
            <QuantityTransactionsTable transactions={quantityTransactions} />
            <TablePaginationControls
              currentPage={currentPageQuantity}
              setPage={(page) => {
                setCurrentPageQuantity(page)
                fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.QUANTITY, page)
              }}
              isLastPage={isLastPageQuantity}
              startFromZero={false}
            />
          </Box>

          <Box padding={2}>
            <PriceTransactionsTable transactions={priceTransactions} />
            <TablePaginationControls
              currentPage={currentPagePrice}
              setPage={(page) => {
                setCurrentPagePrice(page)
                fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.PRICE, page)
              }}
              isLastPage={isLastPagePrice}
              startFromZero={false}
            />
          </Box>

          <Box padding={2}>
            <ReservationTransactionsTable transactions={reservationTransactions} />
            <TablePaginationControls
              currentPage={currentPageReservation}
              setPage={(page) => {
                setCurrentPageReservation(page)
                fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.RESERVATION, page)
              }}
              isLastPage={isLastPageReservation}
              startFromZero={false}
            />
          </Box>

          {(userId || user.featureFlags?.taxation) && (
            <Box padding={2}>
              <TaxTransactionsTable transactions={taxTransactions} />
              <TablePaginationControls
                currentPage={currentPageTax}
                setPage={(page) => {
                  setCurrentPageTax(page)
                  fetchInventoryTransactions(INVENTORY_TRANSACTION_TYPE.TAX_TRANSACTION, page)
                }}
                isLastPage={isLastPageTax}
                startFromZero={false}
              />
            </Box>
          )}
        </>
      )}
    </div>
  )
}
